// WebAuthn / Passkeys ("Sign in with Fingerprint") — Web client wrapper.
//
// Registration (enroll, authenticated) and login (public) each are a 2-call ceremony:
//   options from the server -> browser prompts the fingerprint -> verify on the server.
// The server verifies the signature (Option B), so nothing here can be spoofed by
// tampering localStorage. On login the server returns a Firebase custom token which the
// caller exchanges via signInWithCustomToken() — exactly like trusted-device login.

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';
import { API_ENDPOINTS, apiClient } from '../config/api';

// localStorage flag: set once this browser has a passkey enrolled, so the login
// screen knows to offer the fingerprint button. Cleared if a login finds no passkey.
export const WEB_PASSKEY_FLAG = 'webPasskeyEnabled';

// localStorage flag: set when the user explicitly ticks "Don't ask again"
// on the post-login enrollment prompt. The prompt is suppressed on
// subsequent logins on this browser once the flag is set. Cleared
// automatically the moment a passkey enrolls (successful enroll implies
// the user wanted this after all).
export const WEB_PASSKEY_PROMPT_DISMISSED_FLAG = 'webPasskeyPromptDismissed';

export const hasDismissedPasskeyPrompt = () =>
  localStorage.getItem(WEB_PASSKEY_PROMPT_DISMISSED_FLAG) === 'true';
export const dismissPasskeyPrompt = () => {
  try { localStorage.setItem(WEB_PASSKEY_PROMPT_DISMISSED_FLAG, 'true'); } catch { /* ignore */ }
};
export const clearPasskeyPromptDismissal = () => {
  try { localStorage.removeItem(WEB_PASSKEY_PROMPT_DISMISSED_FLAG); } catch { /* ignore */ }
};

/** True if this browser can do WebAuthn at all. */
export const isWebAuthnSupported = () => {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
};

/** True if this device has a built-in (platform) authenticator, e.g. Windows Hello / Touch ID. */
export const isPlatformAuthenticatorAvailable = async () => {
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
};

/** Whether we've previously enrolled a passkey in THIS browser. */
export const hasEnrolledPasskey = () => localStorage.getItem(WEB_PASSKEY_FLAG) === 'true';

/**
 * Enroll a passkey for the currently signed-in user (authenticated).
 * @param {() => Promise<string>} getToken - resolves the current Firebase ID token.
 * @returns {Promise<void>} resolves on success; throws on failure/cancellation.
 */
export const enrollPasskey = async (getToken) => {
  const token = await getToken();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // 1. Ask the server for creation options.
  const optionsRes = await apiClient.post(API_ENDPOINTS.webauthnRegisterOptions, {}, authHeader);
  const optionsJSON = optionsRes.data;

  // 2. Prompt the fingerprint / device unlock and create the credential.
  const credential = await startRegistration({ optionsJSON });

  // 3. Send the attestation back for server-side verification + storage.
  await apiClient.post(
    API_ENDPOINTS.webauthnRegisterVerify,
    { credentialJson: JSON.stringify(credential), deviceName: getBrowserName() },
    authHeader,
  );

  localStorage.setItem(WEB_PASSKEY_FLAG, 'true');
  // A successful enrollment means the user is on board — clear any prior
  // "don't ask again" so future browser resets don't silently hide the option.
  clearPasskeyPromptDismissal();
};

/**
 * Log in with a passkey (public / pre-auth).
 * @param {string} email
 * @returns {Promise<string|null>} a Firebase custom token, or null if unavailable/failed.
 */
export const loginWithPasskey = async (email) => {
  // 1. Ask the server for login options for this email.
  const optionsRes = await apiClient.post(API_ENDPOINTS.webauthnLoginOptions, { email });
  const optionsJSON = optionsRes.data;

  if (!optionsJSON || !Array.isArray(optionsJSON.allowCredentials) || optionsJSON.allowCredentials.length === 0) {
    // No passkey registered for this account in this account's list — nothing to do.
    localStorage.removeItem(WEB_PASSKEY_FLAG);
    return null;
  }

  // 2. Prompt the fingerprint and produce the assertion.
  const assertion = await startAuthentication({ optionsJSON });

  // 3. Verify server-side; get a Firebase custom token back.
  const verifyRes = await apiClient.post(API_ENDPOINTS.webauthnLoginVerify, {
    email,
    credentialJson: JSON.stringify(assertion),
  });
  return verifyRes.data?.customToken || null;
};

/**
 * List the current user's registered passkeys (server = source of truth).
 * @param {() => Promise<string>} getToken
 * @returns {Promise<Array>} passkey metadata objects (may be empty).
 */
export const listPasskeys = async (getToken) => {
  const token = await getToken();
  if (!token) return [];
  const res = await apiClient.get(API_ENDPOINTS.webauthnCredentials, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data?.passkeys || [];
};

/**
 * Remove one of the current user's passkeys by its document id.
 * @param {() => Promise<string>} getToken
 * @param {string} id
 */
export const removePasskey = async (getToken, id) => {
  const token = await getToken();
  await apiClient.delete(API_ENDPOINTS.webauthnCredentialById(id), {
    headers: { Authorization: `Bearer ${token}` },
  });
};

const getBrowserName = () => {
  const ua = navigator.userAgent;
  let browser = 'Web Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera\//.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  const platform = navigator.platform || '';
  return platform ? `${browser} on ${platform}` : browser;
};
